import {
  insertPositionToIndex,
  type InsertToPosition,
} from '@blocksuite/affine-shared/utils';
import { computed, type ReadonlySignal, signal } from '@preact/signals-core';
import { isCanonicalTablePropertyVisible } from '@streamlain/database-identity/database-view-defaults';

import type { GroupBy } from '../../core/common/types.js';
import type { DataSourceWindowRequest } from '../../core/data-source/base.js';
import { clampDataSourceWindowOffset } from '../../core/data-source/window.js';
import { generateDefaultValues } from '../../core/filter/generate-default-values.js';
import { FilterTrait, filterTraitKey } from '../../core/filter/trait.js';
import type { FilterGroup } from '../../core/filter/types.js';
import { normalizeFilterGroup } from '../../core/filter/utils.js';
import {
  GroupTrait,
  groupTraitKey,
  sortByManually,
} from '../../core/group-by/trait.js';
import { fromJson } from '../../core/property/utils';
import { SortManager, sortTraitKey } from '../../core/sort/manager.js';
import type { Sort } from '../../core/sort/types.js';
import { PropertyBase } from '../../core/view-manager/property.js';
import { type Row, RowBase } from '../../core/view-manager/row.js';
import {
  type SingleView,
  SingleViewBase,
  type SingleViewPropertiesWindow,
} from '../../core/view-manager/single-view.js';
import type { ViewManager } from '../../core/view-manager/view-manager.js';
import { DEFAULT_COLUMN_MIN_WIDTH, DEFAULT_COLUMN_WIDTH } from './consts.js';
import type { TableViewData } from './define.js';

export class TableSingleView extends SingleViewBase<TableViewData> {
  propertiesRaw$: ReadonlySignal<TableProperty[]> = computed(() => {
    const needShow = new Set(this.getBoundedPropertyIdsForView());
    const result: string[] = [];
    const configuredOrder = this.data$.value?.columns.map(v => v.id);
    configuredOrder?.forEach(id => {
      if (needShow.has(id)) {
        result.push(id);
        needShow.delete(id);
      }
    });
    result.push(...needShow);
    return result.map(id => this.propertyGetOrCreate(id));
  });

  properties$: ReadonlySignal<TableProperty[]> = computed(() => {
    return this.propertiesRaw$.value.filter(property => !property.hide$.value);
  });

  private getRawPropertiesWindowForRequest(
    request: DataSourceWindowRequest
  ): SingleViewPropertiesWindow {
    const sourceWindow = this.dataSource.getPropertyIdsWindow(request);
    const needShow = new Set(sourceWindow.propertyIds);
    const orderedIds: string[] = [];
    const configuredOrder = this.data$.value?.columns.map(column => column.id);
    configuredOrder?.forEach(id => {
      if (needShow.has(id)) {
        orderedIds.push(id);
        needShow.delete(id);
      }
    });
    orderedIds.push(...needShow);
    const windowProperties = orderedIds.map(id => this.propertyGetOrCreate(id));
    return {
      offset: sourceWindow.offset,
      limit: sourceWindow.limit,
      totalCount: sourceWindow.totalCount,
      propertyIds: windowProperties.map(property => property.id),
      properties: windowProperties,
    };
  }

  override getPropertiesWindow(
    request: DataSourceWindowRequest
  ): SingleViewPropertiesWindow {
    const rawWindow = this.getRawPropertiesWindowForRequest(request);
    const windowProperties = rawWindow.properties.filter(
      property => !property.hide$.value
    );
    return {
      ...rawWindow,
      propertyIds: windowProperties.map(property => property.id),
      properties: windowProperties,
    };
  }

  override getBoundedPropertiesWindow(): SingleViewPropertiesWindow {
    const limit = this.propertyWindowLimit$.value;
    const offset = clampDataSourceWindowOffset(
      this.columnCount$.value,
      this.propertyWindowOffset$.value,
      limit
    );
    return this.getPropertiesWindow({ offset, limit });
  }

  override getRawPropertiesWindow(
    request: DataSourceWindowRequest
  ): SingleViewPropertiesWindow {
    return this.getRawPropertiesWindowForRequest(request);
  }

  override getBoundedRawPropertiesWindow(): SingleViewPropertiesWindow {
    const limit = this.propertyWindowLimit$.value;
    const offset = clampDataSourceWindowOffset(
      this.columnCount$.value,
      this.propertyWindowOffset$.value,
      limit
    );
    return this.getRawPropertiesWindow({ offset, limit });
  }

  private readonly filter$ = computed(() => {
    return normalizeFilterGroup(this.data$.value?.filter);
  });

  private readonly groupBy$ = computed(() => {
    return this.data$.value?.groupBy;
  });

  private readonly sortList$ = computed(() => {
    return this.data$.value?.sort;
  });

  protected override getProjectionFilter(): FilterGroup | undefined {
    return this.filter$.value;
  }

  protected override getProjectionGroupBy(): GroupBy | undefined {
    return this.groupBy$.value;
  }

  protected override getProjectionSort(): Sort | undefined {
    return this.sortList$.value;
  }

  private readonly sortManager = this.traitSet(
    sortTraitKey,
    new SortManager(this.sortList$, this, {
      setSortList: sortList => {
        this.dataUpdate(data => {
          return {
            sort: {
              ...data.sort,
              ...sortList,
            },
          };
        });
      },
    })
  );

  detailProperties$: ReadonlySignal<TableProperty[]> = computed(() => {
    return this.propertiesRaw$.value.filter(
      property => property.type$.value !== 'title'
    );
  });

  filterTrait = this.traitSet(
    filterTraitKey,
    new FilterTrait(this.filter$, this, {
      filterSet: (filter: FilterGroup) => {
        this.dataUpdate(() => {
          return {
            filter,
          };
        });
      },
    })
  );

  groupTrait = this.traitSet(
    groupTraitKey,
    new GroupTrait(this.groupBy$, this, {
      groupBySet: groupBy => {
        this.dataUpdate(() => {
          return {
            groupBy,
          };
        });
      },
      sortGroup: (ids, asc) => {
        const sorted = sortByManually(
          ids,
          v => v,
          this.groupProperties.map(v => v.key)
        );
        // If descending order is requested, reverse the sorted array
        return asc === false ? sorted.reverse() : sorted;
      },
      sortRow: (key, rows) => {
        const property = this.groupProperties.find(v => v.key === key);
        return sortByManually(
          rows,
          v => v.rowId,
          property?.manuallyCardSort ?? []
        );
      },
      changeGroupSort: keys => {
        const map = new Map(this.groupProperties.map(v => [v.key, v]));
        this.dataUpdate(() => {
          return {
            groupProperties: keys.map(key => {
              const property = map.get(key);
              if (property) {
                return property;
              }
              return {
                key,
                hide: false,
                manuallyCardSort: [],
              };
            }),
          };
        });
      },
      changeRowSort: (groupKeys, groupKey, keys) => {
        const map = new Map(this.groupProperties.map(v => [v.key, v]));
        this.dataUpdate(() => {
          return {
            groupProperties: groupKeys.map(key => {
              if (key === groupKey) {
                const group = map.get(key);
                return group
                  ? {
                      ...group,
                      manuallyCardSort: keys,
                    }
                  : {
                      key,
                      hide: false,
                      manuallyCardSort: keys,
                    };
              } else {
                return (
                  map.get(key) ?? {
                    key,
                    hide: false,
                    manuallyCardSort: [],
                  }
                );
              }
            }),
          };
        });
      },
      changeGroupHide: (key, hide) => {
        this.dataUpdate(() => {
          const list = [...this.groupProperties];
          const idx = list.findIndex(g => g.key === key);
          if (idx >= 0) {
            const target = list[idx];
            if (!target) {
              return { groupProperties: list };
            }
            list[idx] = { ...target, hide };
          } else {
            const order = (this.groupTrait.groupsDataListAll$.value ?? [])
              .map(g => g?.key)
              .filter((k): k is string => !!k);
            let insertPos = 0;
            for (const k of order) {
              if (k === key) break;
              if (list.some(g => g.key === k)) insertPos++;
            }
            list.splice(insertPos, 0, { key, hide, manuallyCardSort: [] });
          }
          return { groupProperties: list };
        });
      },
    })
  );

  mainProperties$ = computed(() => {
    return (
      this.data$.value?.header ?? {
        titleColumn: this.getBoundedRawPropertiesWindow().properties.find(
          property => property.type$.value === 'title'
        )?.id,
        iconColumn: 'type',
      }
    );
  });

  /**
   * Cell display mode for the table view.
   * Default: 'expanded' (show full content)
   */
  cellDisplayMode$ = computed((): 'collapsed' | 'expanded' | 'smart' => {
    return this.data$.value?.cellDisplayMode ?? 'expanded';
  });

  cellDisplayModeSet(mode: 'collapsed' | 'expanded' | 'smart'): void {
    this.dataUpdate(data => ({
      ...data,
      cellDisplayMode: mode,
    }));
  }

  /** @deprecated Use cellDisplayMode$ instead */
  cellsExpanded$ = computed(() => {
    return this.cellDisplayMode$.value === 'expanded';
  });

  /** @deprecated Use cellDisplayModeSet instead */
  cellsExpandedSet(expanded: boolean): void {
    this.cellDisplayModeSet(expanded ? 'expanded' : 'collapsed');
  }

  readonly$ = computed(() => {
    return this.manager.readonly$.value;
  });

  get groupProperties() {
    return this.data$.value?.groupProperties ?? [];
  }

  get name(): string {
    return this.data$.value?.name ?? '';
  }

  override get type(): string {
    return this.data$.value?.mode ?? 'table';
  }

  constructor(viewManager: ViewManager, viewId: string) {
    super(viewManager, viewId);
  }

  isShow(rowId: string, context?: { nowMs: number }): boolean {
    return this.isRowVisibleByFilter(rowId, this.filter$.value, context);
  }

  propertyGetOrCreate(columnId: string): TableProperty {
    return new TableProperty(this, columnId);
  }

  override rowGetOrCreate(rowId: string): TableRow {
    return new TableRow(this, rowId);
  }

  override rowAdd(
    insertPosition: InsertToPosition | number,
    groupKey?: string
  ): string {
    const id = super.rowAdd(insertPosition);

    const filter = this.filter$.value;
    if (filter.conditions.length > 0) {
      const defaultValues = generateDefaultValues(filter, this.vars$.value);
      Object.entries(defaultValues).forEach(([propertyId, jsonValue]) => {
        const property = this.propertyGetOrCreate(propertyId);
        const propertyMeta = property.meta$.value;
        if (propertyMeta) {
          const value = fromJson(propertyMeta.config, {
            value: jsonValue,
            data: property.data$.value,
            dataSource: this.dataSource,
          });
          this.cellGetOrCreate(id, propertyId).valueSet(value);
        }
      });
    }

    if (groupKey && id) {
      this.groupTrait.addToGroup(id, groupKey);
    }
    return id;
  }

  override rowsMapping(rows: Row[]) {
    return this.sortManager.sort(super.rowsMapping(rows));
  }

  getColumnsForData(data: TableViewData): TableColumnData[] {
    const ids = new Set(this.dataSource.getAllPropertyIds());
    const columns = data.columns.map(column => ({ ...column }));
    for (const column of columns) ids.delete(column.id);
    for (const id of ids) {
      const type = this.dataSource.propertyTypeGet(id);
      columns.push({
        id,
        hide: !isCanonicalTablePropertyVisible(type ?? '', undefined),
        width: type === 'title' ? 260 : DEFAULT_COLUMN_WIDTH,
      });
    }
    return columns;
  }

  override placeProperty(
    data: TableViewData,
    propertyId: string,
    position: InsertToPosition
  ): Partial<TableViewData> {
    const columns = this.getColumnsForData(data);
    const previous = columns.findIndex(column => column.id === propertyId);
    const column = columns[previous] ?? {
      id: propertyId,
      hide: false,
      width: DEFAULT_COLUMN_WIDTH,
    };
    if (previous >= 0) columns.splice(previous, 1);
    columns.splice(insertPositionToIndex(position, columns), 0, column);
    return { columns };
  }

  readonly computedProperties$: ReadonlySignal<TableColumnData[]> = computed(
    () => {
      return this.propertiesRaw$.value.map(property => {
        return {
          id: property.id,
          hide: property.hide$.value,
          width: property.width$.value,
          statCalcType: property.statCalcOp$.value,
        };
      });
    }
  );
}

type TableColumnData = TableViewData['columns'][number];

export class TableProperty extends PropertyBase {
  override hideSet(hide: boolean): void {
    this.viewDataUpdate(data => {
      return {
        ...data,
        hide,
      };
    });
  }
  override move(position: InsertToPosition): void {
    this.tableView.dataUpdate(data =>
      this.tableView.placeProperty(data, this.id, position)
    );
  }

  hide$ = computed(() => {
    const hideFromViewData = this.viewData$.value?.hide;
    return !isCanonicalTablePropertyVisible(
      this.type$.value,
      hideFromViewData ?? undefined
    );
  });

  statCalcOp$ = computed(() => {
    return this.viewData$.value?.statCalcType;
  });

  private readonly transientWidth$ = signal<number | undefined>(undefined);

  width$: ReadonlySignal<number> = computed(() => {
    const transientWidth = this.transientWidth$.value;
    if (transientWidth != null) {
      return transientWidth;
    }
    const column = this.viewData$.value;
    if (column?.width != null) {
      return column.width;
    }
    const type = this.type$.value;
    if (type === 'title') {
      return 260;
    }
    return DEFAULT_COLUMN_WIDTH;
  });

  get minWidth() {
    return this.meta$.value?.config.minWidth ?? DEFAULT_COLUMN_MIN_WIDTH;
  }

  constructor(
    private readonly tableView: TableSingleView,
    columnId: string
  ) {
    super(tableView as SingleView, columnId);
  }

  viewDataUpdate(
    updater: (viewData: TableColumnData) => Partial<TableColumnData>
  ): void {
    this.tableView.dataUpdate(data => {
      return {
        ...data,
        columns: this.tableView
          .getColumnsForData(data)
          .map(v => (v.id === this.id ? { ...v, ...updater(v) } : v)),
      };
    });
  }

  viewData$ = computed(() => {
    return this.tableView.data$.value?.columns.find(v => v.id === this.id);
  });

  updateStatCalcOp(type?: string): void {
    this.viewDataUpdate(data => {
      return {
        ...data,
        statCalcType: type,
      };
    });
  }

  updateWidth(width: number): void {
    this.viewDataUpdate(data => {
      return {
        ...data,
        width,
      };
    });
    this.transientWidth$.value = undefined;
  }

  previewWidth(width: number): void {
    this.transientWidth$.value = width;
  }

  clearWidthPreview(): void {
    this.transientWidth$.value = undefined;
  }
}
export class TableRow extends RowBase {
  override move(
    position: InsertToPosition,
    fromGroup?: string,
    toGroup?: string
  ): void {
    if (toGroup == null) {
      super.move(position);
      return;
    }
    this.tableView.groupTrait.moveCardTo(
      this.rowId,
      fromGroup,
      toGroup,
      position
    );
  }
  constructor(
    readonly tableView: TableSingleView,
    rowId: string
  ) {
    super(tableView, rowId);
  }
}
